import { Promotion, Product } from "./types"

const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:5000"

/**
 * Récupère la meilleure promotion applicable pour un produit donné
 * Utilise la nouvelle API qui retourne les promotions par priorité (PRODUCT > CATEGORY > GENERAL)
 */
export async function getActivePromotionForProduct(productId: string): Promise<Promotion | null> {
    try {
        // Utiliser le nouvel endpoint qui trouve automatiquement la meilleure promotion
        const response = await fetch(`${API_URL}/promotions/product/${productId}/applicable`)
        const data = await response.json()

        if (data.success && data.promotion) {
            return data.promotion
        }

        return null
    } catch (error) {
        console.error("Error fetching active promotions:", error)
        return null
    }
}

/**
 * Récupère toutes les promotions applicables pour un produit (pour debugging/affichage)
 */
export async function getAllApplicablePromotions(productId: string): Promise<Promotion[]> {
    try {
        const response = await fetch(`${API_URL}/promotions/product/${productId}/applicable`)
        const data = await response.json()

        if (data.success && data.allApplicable) {
            return data.allApplicable
        }

        return []
    } catch (error) {
        console.error("Error fetching applicable promotions:", error)
        return []
    }
}

/**
 * Calcule le prix après réduction
 */
export function calculateDiscountedPrice(originalPrice: number, promotion: Promotion): number {
    if (promotion.discountType === "PERCENTAGE") {
        return originalPrice * (1 - promotion.discountValue / 100)
    } else {
        return Math.max(0, originalPrice - promotion.discountValue)
    }
}

/**
 * Calcule le montant économisé
 */
export function calculateSavings(originalPrice: number, promotion: Promotion): number {
    return originalPrice - calculateDiscountedPrice(originalPrice, promotion)
}

/**
 * Formate le badge de réduction
 */
export function formatDiscountBadge(promotion: Promotion): string {
    if (promotion.discountType === "PERCENTAGE") {
        return `-${promotion.discountValue}%`
    } else {
        return `-${promotion.discountValue} FCFA`
    }
}

/**
 * Vérifie si une promotion est encore valide (avec marge de sécurité)
 */
export function isPromotionValid(promotion: Promotion): boolean {
    const now = new Date()
    const endDate = new Date(promotion.endDate)

    return (
        promotion.isActive &&
        now <= endDate &&
        (!promotion.maxUsageCount || promotion.currentUsageCount < promotion.maxUsageCount)
    )
}

/**
 * Calcule le temps restant pour une promotion
 */
export function getTimeRemaining(endDate: Date | string): { days: number; hours: number; minutes: number; seconds: number } {
    const now = new Date().getTime()
    const end = new Date(endDate).getTime()
    const distance = end - now

    if (distance <= 0) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0 }
    }

    return {
        days: Math.floor(distance / (1000 * 60 * 60 * 24)),
        hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((distance % (1000 * 60)) / 1000),
    }
}
